import type { PortableTextBlock } from '@portabletext/types';
import { LONG_MONTHS } from '../constants/index';

// -------------------
// DATES
// -------------------
/**
 * Turn a date string into a date string with format: DD/MM/YYYY
 * @Param dateString with format YYYYMMDD
 * @returns string
 */
export function formatDateString(dateString: string): string {
  const year = +dateString.substring(0, 4);
  const month = +dateString.substring(4, 6);
  const day = +dateString.substring(6, 8);

  return `${`0${day}`.slice(-2)}/${`0${month}`.slice(-2)}/${year}`;
}

export const dateToAusString = (date: Date): string => {
  return date.toLocaleDateString('en-AU');
};

/**
 * get name of month and year from date string
 * @Param date is a string with format YYYYMMDD
 * @returns string with format of Month YYYY
 */
export const getMonthAndYearFromDateString = (date: string) => {
  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const monthName = new Date(+year, +month - 1).toLocaleString('default', {
    month: 'long',
  });
  return `${monthName} ${year}`;
};

/**
 * get long date from date string
 * @Param date is a string with format YYYYMMDD
 * @returns string with format of Month YYYY
 */
export const getLongDateFromDateString = (date: string) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const monthName = new Date(year, month).toLocaleString('default', {
    month: 'long',
  });
  return `${monthName} ${dateObj.getDate()}, ${year}`;
};

export const formatDateAsSentence = (date: Date) => {
  const dateIndex = date.getDate();
  const month = LONG_MONTHS[date.getMonth()];
  const year = date.getFullYear();

  let suffix = 'th';
  
  // Special case for 11, 12, 13
  if (dateIndex >= 11 && dateIndex <= 13) {
    suffix = 'th';
  } else if (dateIndex % 10 === 1) {
    suffix = 'st';
  } else if (dateIndex % 10 === 2) {
    suffix = 'nd';
  } else if (dateIndex % 10 === 3) {
    suffix = 'rd';
  }

  return `${dateIndex}${suffix} ${month} ${year}`;
};

// -------------------
// TEXT
// -------------------
export function TrimText(
  value: string,
  maxLength: number,
  ellipsis = true
): string {
  if (!value) return '';
  if (value.length > maxLength) {
    if (ellipsis) {
      return `${value.slice(0, maxLength - 3)}...`;
    }
    return `${value.slice(0, maxLength)}`;
  }
  return value;
}

/**
 * Capitalises the first letter of a string
 * @param str
 * @returns Str
 */
export function Capitalise(str: string): string {
  return `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`;
}

/**
 * Capitalise each word in a sentence
 * @param str
 * @param ignoredWords array of words to ignore. e.g. ['and', 'if']
 * @returns Str In Capitals
 */
export function CapitaliseSentence(
  str: string,
  ignoredWords?: string[]
): string {
  return str
    .split(' ')
    .map((s) => (ignoredWords?.includes(s) ? s : Capitalise(s)))
    .join(' ');
}

/**
 * Take portable text and join together as a string
 * @param blocks
 * @returns
 */
export function portableTextToPlainText(
  blocks: PortableTextBlock[] = []
): string {
  return (
    blocks
      // loop through each block
      .map((block) => {
        // if it's not a text block with children,
        // return nothing
        if (block._type !== 'block' || !block.children) {
          return '';
        }
        // loop through the children spans, and join the
        // text strings
        return block.children.map((child) => child.text).join('');
      })
      // join the paragraphs leaving split by two linebreaks
      .join('\n\n')
  );
}

/**
 * Take a number value and return it as currency string $x.xx
 * @param value
 * @returns
 */
export function money(value: number | string | null): string {
  return Number(value).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  });
}

// -------------------
// URLS
// -------------------
export function formatUrl(value: string): string {
  if (value[0] === '/') {
    return value;
  }
  return `/${value}`;
}

export const normaliseRoute = (path: string): string => {
  if (path) {
    return path.slice(-1) === '/' ? path.slice(0, -1) : path;
  }
  console.error('Error: no path was provided on element, a slug is missing');
  // In the absolute worst case scenario where an item doesnt have a slug, have route to 404
  return '/404';
};

export const getBasePath = (path: string): string => {
  const queryIndex = path.indexOf('?');
  return queryIndex > -1 ? path.slice(0, queryIndex) : path;
};

export const normaliseSlug = (slug: string) =>
  slug.length > 1 ? slug.replace(/\/$/, '') : slug;
